import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import {
  User,
  Mail,
  Phone,
  Building,
  Tag,
  Briefcase,
  Users,
  Calendar,
  Folder,
  FileText,
  Linkedin,
  Instagram,
  Twitter,
  Facebook,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { AskAiButton } from "../../../../chat";
import clsx from "clsx";
import LabelsSelect from "../../../../components/LabelsSelect";
import TaskAction from "../../../../actions/TaskAction";
import BucketAction from "../../../../actions/BucketAction";
import EditableDetailRow from "./../EditableDetailRow";
import EmailInputField from "./../EmailInputField";
import PhoneInputField from "./../PhoneInputField";
import SelectInline from "./../SelectInline";
import AutoTextArea from "./../AutoTextArea";
import Avatar from "../../../../components/Avatar";
import CustomField from "../../../../main/CustomField";
import { FileCode } from "lucide-react";
import DatePicker from "./../DatePicker";
import { format, isValid, parseISO } from "date-fns";
import InputInline from "../../../../components/InputInline";
import AssigneeSelectV2 from "../../../../components/AssigneeSelect";
import DetailPanelSkeleton from "./DetailPanelSkeleton";
import BucketSelect from "../../../../components/BucketSelect";
import AvatarUploadRow from "./../AvatarUploadRow";
import Card from "../../../../components/ui/Card";
import Tabs from "../../../../components/ui/Tabs";
import { AccountDetail } from "./AccountDetailPanel";
import ItemSelect from "../../../../components/ItemSelect";
import { navigateToItem } from "../../../../Navigator";
import ItemLinkAction from "../../../../actions/ItemLinkAction";

interface ContactDetailPanelProps {
  projectId: string;
  contentType: string; // expect "CONTACT"
}

// Helpers
const toStr = (v: any) => (v === null || v === undefined ? "" : String(v));
const normalizeOptions = (opts: any[]): { value: string; label: string }[] => {
  if (!Array.isArray(opts)) return [];
  return opts.map((o: any) => ({
    value: toStr(o?.value),
    label: toStr(o?.label),
  }));
};

// Extract username from social media URL
// e.g., "https://www.instagram.com/hoanganh993/" => "hoanganh993"
const extractSocialUsername = (url: string): string => {
  if (!url) return "";
  const cleanUrl = url.replace(/\/+$/, "");
  const patterns = [
    /(?:instagram\.com|facebook\.com|twitter\.com|x\.com|linkedin\.com)\/(?:in\/)?([^/?]+)/i,
    /([^/]+)$/,
  ];
  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return url;
};

const ContactDetail = ({ projectId, contentType, isLoading, task, visibleRowNumber }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const members = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "members"]) || fromJS([])
  );

  const fields = useSelector((state: any) => state.projectReducer.getIn(["project", "fields"]));

  const buckets = useSelector((state: any) => state.bucketReducer.get(contentType)) || fromJS([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- salutationsRaw used in useMemo
  const salutationsRaw =
    useSelector((state: any) => state.ruleReducer.getIn(["salutation"]))?.toJS() || [];

  // Ensure buckets are loaded
  useEffect(() => {
    if (projectId && contentType && (!buckets || buckets.size === 0)) {
      dispatch(BucketAction.getBuckets(projectId, contentType));
    }
  }, [dispatch, projectId, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = (task?.get?.("data") as any) || fromJS({});
  const getVal = (fieldId: string) => data.getIn([fieldId, "value"]);

  // Normalized options for selects
  const salutationOptions = useMemo(() => normalizeOptions(salutationsRaw), [salutationsRaw]);

  // Current values
  const iconUrl = toStr(task?.get?.("iconUrl") || "");
  const contactNameRaw = getVal("name");
  const contactName = contactNameRaw || t("Untitled");
  const email = getVal("email") || "";
  const phone = getVal("phone") || "";
  const jobTitle = getVal("jobTitle") || "";
  const description = getVal("description") || "";
  const salutation = getVal("salutation") || "";
  const birthday = getVal("birthday") || "";

  // Social media fields
  const linkedIn = getVal("linkedIn") || "";
  const instagram = getVal("instagram") || "";
  const twitter = getVal("twitter") || "";
  const facebook = getVal("facebook") || "";

  // Salutation label
  const salutationLabel = useMemo(() => {
    const f = salutationOptions.find((o) => toStr(o.value) === toStr(salutation));
    if (f?.label) return f.label;
    if (salutation) return toStr(salutation);
    return <span className="text-gray-400">{t("Set a value...")}</span>;
  }, [salutationOptions, salutation, t]);

  // Assignee display names (robust parsing for string | array | immutable)
  const assigneeIds: string[] = (() => {
    const toIdArr = (v: any): string[] => {
      if (!v) return [];
      if (Array.isArray(v)) return v.map((x) => String(x)).filter(Boolean);
      if (typeof v?.toJS === "function") {
        const x = v.toJS();
        if (Array.isArray(x)) return x.map((s: any) => String(s)).filter(Boolean);
        if (typeof x === "string")
          return x
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean);
        return [];
      }
      if (typeof v === "string")
        return v
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [];
    };
    return toIdArr(getVal("assignees")).map((s) => String(s));
  })();
  const assigneeMatches = assigneeIds.map((id) => {
    const mByAccount = members.find((m: any) => String(m.get("accountId")) === String(id));
    const mById = mByAccount || members.find((m: any) => String(m.get("id")) === String(id));
    const mByValue = mById || members.find((m: any) => String(m.get("value")) === String(id));
    const found = mByValue || null;
    return {
      accountId: String(id),
      displayName:
        found?.get?.("displayName") || found?.get?.("name") || found?.get?.("email") || String(id),
      email: found?.get?.("email"),
      avatarUrl: found?.get?.("avatarUrl"),
    };
  });

  // Render assignees with avatars
  const renderAssigneeDisplay = () => {
    if (assigneeMatches.length === 0) {
      return <span className="text-gray-400">{t("Unassigned")}</span>;
    }

    const maxVisible = 2;
    const visibleAssignees = assigneeMatches.slice(0, maxVisible);
    const remainingCount = assigneeMatches.length - maxVisible;

    return (
      <div className="flex items-center gap-x-2 overflow-hidden">
        {visibleAssignees.map((user) => (
          <div
            key={`assignee_${user.accountId}`}
            className="flex items-center space-x-1.5 shrink-0"
          >
            <Avatar sizeIcon={4} avatarUrl={user.avatarUrl} displayName={user.displayName} />
            <span className="text-[12px] leading-4 font-medium text-gray-800 whitespace-nowrap">
              {user.displayName}
            </span>
          </div>
        ))}
        {remainingCount > 0 && (
          <div className="flex items-center space-x-1.5 shrink-0">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-[10px] font-medium text-gray-600">+{remainingCount}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const birthdayText = (() => {
    if (!birthday) return <span className="text-gray-400">{t("Set a value...")}</span>;

    try {
      let date: Date | undefined;
      const birthdayType = typeof birthday;

      if (birthdayType === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
          date = parseISO(birthday);
        } else {
          date = new Date(birthday);
        }
      } else if (birthdayType === "number") {
        const num = birthday as number;
        const ms = num < 1e12 ? num * 1000 : num;
        date = new Date(ms);
      } else {
        date = new Date(birthday);
      }

      // Validate date: must be valid and within reasonable range (1990-2100)
      if (!isValid(date)) return <span className="text-gray-400">{t("Set a value...")}</span>;
      const year = date.getFullYear();
      if (year < 1990 || year > 2100)
        return <span className="text-gray-400">{t("Set a value...")}</span>;

      return format(date, "dd MMM yyyy");
    } catch {
      return <span className="text-gray-400">{t("Set a value...")}</span>;
    }
  })();

  // Format email with link style
  const formatEmailDisplay = (emailValue: string) => {
    if (!emailValue) return <span className="text-gray-400">{t("Set a value...")}</span>;
    return (
      <a
        href={`mailto:${emailValue}`}
        className="text-blue-600 hover:text-blue-700 underline decoration-gray-200 underline-offset-2 cursor-pointer"
        onClick={(e) => {
          // Allow Ctrl+Click or middle-click to open link
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let default behavior happen
          }
          // Otherwise, prevent default and let event bubble to open edit mode
          e.preventDefault();
        }}
      >
        {emailValue}
      </a>
    );
  };

  // Format phone with link style
  const formatPhoneDisplay = (phoneValue: string) => {
    if (!phoneValue) return <span className="text-gray-400">{t("Set a value...")}</span>;
    return (
      <a
        href={`tel:${phoneValue}`}
        className="text-blue-600 hover:text-blue-700 underline decoration-gray-200 underline-offset-2 cursor-pointer"
        onClick={(e) => {
          // Allow Ctrl+Click or middle-click to open link
          if (e.ctrlKey || e.metaKey || e.button === 1) {
            return; // Let default behavior happen
          }
          // Otherwise, prevent default and let event bubble to open edit mode
          e.preventDefault();
        }}
      >
        {phoneValue}
      </a>
    );
  };

  // Update
  const handleUpdateField = async (fieldId: string, fieldValue: any) => {
    const itemId = task.get("id");
    if (itemId) {
      await dispatch(TaskAction.updateTask(itemId, fieldId, fieldValue));
    }
  };

  // Get task field value (for custom fields)
  const getTaskFieldValue = (fieldId: string) => {
    return getVal(fieldId);
  };

  // Render custom fields
  const renderCustomFields = () => {
    if (!fields) return [];

    const customFields = fields.filter(
      (field: any) => field.get("isCustomField") && field.get("formType") === contentType
    );

    if (!customFields || customFields.size === 0) return [];

    return customFields
      .map((customField: any) => {
        const fieldId = customField.get("id");
        const fieldData = {
          ...customField.toJS(),
          value: getTaskFieldValue(fieldId),
        };

        // Format value for display
        const displayValue = () => {
          const val = getTaskFieldValue(fieldId);
          if (val === null || val === undefined || val === "") {
            return <span className="text-gray-400">{t("Set a value...")}</span>;
          }
          if (customField.get("type") === "boolean") {
            return val === "yes" || val === true ? t("Yes") : t("No");
          }
          if (customField.get("type") === "datetime") {
            return val ? (
              new Date(val).toLocaleDateString()
            ) : (
              <span className="text-gray-400">{t("Set a value...")}</span>
            );
          }
          return String(val);
        };

        return buildRow(
          `custom_${fieldId}`,
          <FileCode className="w-4 h-4 text-gray-500" />,
          t(customField.get("label") || fieldId),
          <div className="w-full">
            <CustomField
              field={fieldData}
              variant="inline"
              onChange={(value: any) => {
                handleUpdateField(fieldId, value);
              }}
            />
          </div>,
          displayValue()
        );
      })
      .toArray();
  };

  // Row builder
  const buildRow = (
    key: string,
    icon: React.ReactNode,
    label: string,
    editComponent: React.ReactNode,
    valueContent: React.ReactNode,
    usePopover?: boolean,
    actions?: ("edit" | "copy" | "show-all")[]
  ) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <EditableDetailRow
        label={label}
        editComponent={editComponent}
        usePopover={usePopover}
        actions={actions}
      >
        {valueContent}
      </EditableDetailRow>
    </div>
  );

  const buildRowAccount = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <ItemSelect
            projectId={projectId}
            contentType="COMPANY"
            type="INLINE"
            value={getTaskFieldValue("account")?.toJS()}
            onChange={async (account: any) => {
              await handleUpdateField("account", account);
              dispatch(ItemLinkAction.getItemLink(task.get("id")));
            }}
            placeholder={t("No account")}
            isClearable
            onOpenItem={(account: any) => {
              const itemData = fromJS({
                id: account.value || account.id,
                type: "COMPANY",
                projectId: projectId,
              });
              navigateToItem({ item: itemData, navigate });
            }}
          />
        </div>
      </div>
    </div>
  );

  const buildRowLabels = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <LabelsSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getVal("labels")?.toJS() || []}
            onChange={(labelIds: string[]) => handleUpdateField("labels", labelIds)}
            placeholder={t("Select labels")}
          />
        </div>
      </div>
    </div>
  );

  const buildRowBucket = (key: string, icon: React.ReactNode, label: string) => (
    <div key={key} className="flex items-center w-full py-0.5">
      <div className="flex items-center">{icon}</div>
      <div className="flex items-center w-full">
        <div className="w-[150px] text-[12px] leading-4 font-medium text-gray-500 ml-1 py-1.5">
          {label}
        </div>
        <div className="flex-1 min-w-0">
          <BucketSelect
            projectId={projectId}
            contentType={contentType}
            type="INLINE"
            value={getTaskFieldValue("bucketId")}
            onChange={(bucketId: string) => handleUpdateField("bucketId", bucketId)}
            placeholder={t("Select...")}
          />
        </div>
      </div>
    </div>
  );

  const itemId = String(task.get("id") || "");

  const rows: React.ReactNode[] = [
    // Avatar field (shared component)
    <AvatarUploadRow
      key="iconUrl"
      itemId={itemId}
      iconUrl={iconUrl}
      displayName={contactName}
      type="CONTACT"
      onSuccess={() => dispatch(TaskAction.getItem(itemId))}
    />,
    // Basic Information
    buildRow(
      "contactName",
      <User className="w-4 h-4 text-gray-500" />,
      t("Contact Name"),
      <InputInline
        value={contactNameRaw || ""}
        onFinish={(val: string) => {
          if (val && val.trim()) {
            handleUpdateField("name", val);
          }
        }}
        variant="inline"
      />,
      contactName
    ),
    buildRowAccount(
      "account",
      <Building className="w-4 h-4 text-gray-500" />,
      t("Primary Account")
    ),
    buildRow(
      "description",
      <FileText className="w-4 h-4 text-gray-500" />,
      t("Description"),
      <div className="w-full rounded-md min-h-[60px]">
        <AutoTextArea
          rows={3}
          placeholder={t("Add a description...")}
          value={description}
          onChange={(val: string) => handleUpdateField("description", val)}
          className="text-[12px] border-0 leading-4 font-medium text-gray-800 placeholder:text-gray-400 focus:outline-none resize-none"
          autoFocus={true}
        />
      </div>,
      description ? (
        <span className="line-clamp-2">{description}</span>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      true,
      description ? ["show-all"] : undefined
    ),
    // Contact Information
    buildRow(
      "email",
      <Mail className="w-4 h-4 text-gray-500" />,
      t("Email"),
      <EmailInputField
        value={email}
        variant="inline"
        onFinish={(val: string) => {
          handleUpdateField("email", val);
        }}
      />,
      formatEmailDisplay(email),
      undefined,
      ["edit", "copy"]
    ),
    buildRow(
      "phone",
      <Phone className="w-4 h-4 text-gray-500" />,
      t("Phone"),
      <PhoneInputField
        value={phone}
        variant="inline"
        onFinish={(val: string) => handleUpdateField("phone", val)}
      />,
      formatPhoneDisplay(phone),
      undefined,
      ["edit", "copy"]
    ),
    buildRow(
      "jobTitle",
      <Briefcase className="w-4 h-4 text-gray-500" />,
      t("Job Title"),
      <InputInline
        value={jobTitle}
        onFinish={(val: string) => handleUpdateField("jobTitle", val)}
        placeholder={t("Job title")}
        variant="inline"
      />,
      jobTitle ? jobTitle : <span className="text-gray-400">{t("Set a value...")}</span>
    ),
    // Relationships & Classification
    buildRowLabels("labels", <Tag className="w-4 h-4 text-gray-500" />, t("Labels")),
    buildRow(
      "assignees",
      <Users className="w-4 h-4 text-gray-500" />,
      t("Owners"),
      <AssigneeSelectV2
        value={getVal("assignees")}
        autoOpen
        showTriggerHeight={true}
        multiple={true}
        customTrigger={renderAssigneeDisplay()}
        className="z-50"
        onChange={(val) => {
          const ids = Array.isArray(val)
            ? val
            : String(val || "")
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
          handleUpdateField("assignees", ids);
        }}
      />,
      renderAssigneeDisplay()
    ),
    buildRow(
      "salutation",
      <User className="w-4 h-4 text-gray-500" />,
      t("Salutation"),
      <SelectInline
        autoOpen
        customTrigger={salutationLabel}
        options={salutationOptions}
        value={toStr(salutation)}
        onChange={(val: string) => handleUpdateField("salutation", val)}
      />,
      salutationLabel
    ),
    buildRow(
      "birthday",
      <Calendar className="w-4 h-4 text-gray-500" />,
      t("Birthday"),
      <DatePicker
        selected={birthday}
        onChange={(val: string) => handleUpdateField("birthday", val)}
        autoOpen
        variant="inline"
        customTrigger={birthdayText}
        showTriggerHeight={true}
      />,
      birthdayText
    ),
    buildRowBucket("bucketId", <Folder className="w-4 h-4 text-gray-500" />, t("Group")),
    // Social media fields
    buildRow(
      "linkedIn",
      <Linkedin className="w-4 h-4 text-gray-500" />,
      t("LinkedIn"),
      <InputInline
        value={linkedIn}
        onFinish={(val: string) => handleUpdateField("linkedIn", val)}
        placeholder={t("Set a link (e.g., linkedin.com/in/username)")}
        variant="inline"
      />,
      linkedIn ? (
        <a
          href={linkedIn.startsWith("http") ? linkedIn : `https://${linkedIn}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(linkedIn)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    buildRow(
      "instagram",
      <Instagram className="w-4 h-4 text-gray-500" />,
      t("Instagram"),
      <InputInline
        value={instagram}
        onFinish={(val: string) => handleUpdateField("instagram", val)}
        placeholder={t("Set a link (e.g., instagram.com/username)")}
        variant="inline"
      />,
      instagram ? (
        <a
          href={instagram.startsWith("http") ? instagram : `https://${instagram}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(instagram)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    buildRow(
      "twitter",
      <Twitter className="w-4 h-4 text-gray-500" />,
      t("Twitter"),
      <InputInline
        value={twitter}
        onFinish={(val: string) => handleUpdateField("twitter", val)}
        placeholder={t("Set a link (e.g., x.com/username)")}
        variant="inline"
      />,
      twitter ? (
        <a
          href={twitter.startsWith("http") ? twitter : `https://${twitter}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(twitter)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    buildRow(
      "facebook",
      <Facebook className="w-4 h-4 text-gray-500" />,
      t("Facebook"),
      <InputInline
        value={facebook}
        onFinish={(val: string) => handleUpdateField("facebook", val)}
        placeholder={t("Set a link (e.g., facebook.com/username)")}
        variant="inline"
      />,
      facebook ? (
        <a
          href={facebook.startsWith("http") ? facebook : `https://${facebook}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          {extractSocialUsername(facebook)}
        </a>
      ) : (
        <span className="text-gray-400">{t("Set a value...")}</span>
      ),
      false,
      ["edit", "copy"]
    ),
    // Custom Fields
    ...renderCustomFields(),
  ];

  if (!task.get("id")) {
    return (
      <div className="px-3">
        <div className="text-xs text-gray-500 py-4 text-center">{t("No information")}</div>
      </div>
    );
  }

  const visibleRows = visibleRowNumber === -1 ? rows : rows.slice(0, visibleRowNumber);

  return (
    <div className="px-3">
      {isLoading ? (
        <DetailPanelSkeleton rows={visibleRows.length} />
      ) : (
        visibleRows.map((row, idx) => <React.Fragment key={idx}>{row}</React.Fragment>)
      )}
    </div>
  );
};

const ContactDetailPanel: React.FC<ContactDetailPanelProps> = ({ projectId, contentType }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isLoadingTask = useSelector((state: any) => state.taskReducer.get("pending"));
  const task =
    useSelector((state: any) => state.taskReducer.getIn(["task", contentType])) || fromJS({});
  const primaryAccountId =
    task.getIn(["data", "account", "value", "id"]) ||
    task.getIn(["data", "account", "value", "value"]);
  const taskCompany =
    useSelector((state: any) => state.taskReducer.getIn(["task", "COMPANY"])) || fromJS({});

  const [activeTab, setActiveTab] = useState("about");
  const [collapsed, setCollapsed] = useState(false);
  const [internalCollapsed, setInternalCollapsed] = useState(true);

  const LeftIcon = useMemo(() => {
    return (
      <ChevronDown
        className={clsx(
          "w-4 h-4 transition-transform shrink-0",
          collapsed ? "-rotate-90" : "rotate-0"
        )}
      />
    );
  }, [collapsed]);

  useEffect(() => {
    if (primaryAccountId) {
      dispatch(TaskAction.getItem(primaryAccountId));
    } else {
      dispatch(TaskAction.resetTask("COMPANY"));
    }
  }, [dispatch, primaryAccountId]);

  if (contentType !== "CONTACT") return null;

  const recordId = String(task.get("id") || "");
  const contactDisplayName =
    (task.getIn(["data", "name", "value"]) as string | undefined) ||
    (t("Untitled") as string);
  const contactEmail = (task.getIn(["data", "email", "value"]) as string | undefined) || "";
  const contactIconUrl = (task.get("iconUrl") as string | undefined) || undefined;

  return (
    <Card
      bgClass={"bg-white"}
      borderClass={"border border-gray-200"}
      roundedClass={"rounded-[12px]"}
      shadow={"custom"}
      shadowClass={"shadow-[0_1px_1px_0_rgba(0,0,0,0.05)]"}
    >
      <div className="w-full px-3 py-2 flex items-center gap-2">
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          className="text-gray-800 hover:text-gray-700"
        >
          {LeftIcon}
        </button>
        <div className="w-full grid grid-cols-1 gap-4">
          <Tabs
            items={[
              {
                key: "about",
                label: <div className="font-medium">{t("About")}</div>,
              },
              {
                key: "company",
                label: <div className="font-medium">{t("Company")}</div>,
              },
            ]}
            value={activeTab}
            onChange={(key) => {
              setActiveTab(key);
            }}
            size="md"
            headerBorderClass="border-b border-gray-200"
            headerShadow="none"
          />
        </div>
        <AskAiButton
          workspaceId={projectId}
          recordId={recordId}
          recordType="CONTACT"
          displayName={contactDisplayName}
          subtitle={contactEmail || (t("Contact") as string)}
          avatarUrl={contactIconUrl}
          ariaLabel={t("Ask AI about this contact") as string}
          className="shrink-0"
        />
      </div>
      {!collapsed && (
        <>
          {activeTab === "about" && (
            <ContactDetail
              projectId={projectId}
              contentType={contentType}
              isLoading={isLoadingTask}
              task={task}
              visibleRowNumber={internalCollapsed ? 6 : -1}
            />
          )}
          {activeTab === "company" && (
            <AccountDetail
              projectId={projectId}
              contentType={"COMPANY"}
              isLoading={isLoadingTask}
              task={taskCompany}
              visibleRowNumber={internalCollapsed ? 6 : -1}
            />
          )}
          <div className="px-3 pb-3 mt-2">
            <button
              type="button"
              onClick={() => setInternalCollapsed((v) => !v)}
              className="inline-flex items-center gap-1 text-[10px] leading-4 text-gray-500 hover:text-gray-700"
            >
              <span>{internalCollapsed ? t("Expand") : t("Collapse")}</span>
              {internalCollapsed ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronUp className="w-3 h-3" />
              )}
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export default ContactDetailPanel;
